// Formato de arquivo pelos bytes mágicos (spec §7.10–7.11), igual no Worker (PUT /v1/blobs, captura, ingestão) e no
// app (createFile): o tipo que o sistema operacional informa varia (audio/x-m4a, vazio...), os bytes não.
import { ACCEPTED_MIME, type CardKind } from "./types.ts";

/** Bytes lidos para reconhecer o formato (o SVG, texto, precisa de mais que os binários). */
export const SNIFF_BYTES = 1024;

// Marca principal da caixa ftyp (ISO BMFF): imagem HEIF/AVIF, vídeo MP4/MOV ou áudio M4A.
const FTYP: Record<string, string> = {
  avif: "image/avif", avis: "image/avif", heic: "image/heic", heix: "image/heic", hevc: "image/heic", hevx: "image/heic",
  heim: "image/heic", heis: "image/heic", mif1: "image/heif", msf1: "image/heif", heif: "image/heif",
  isom: "video/mp4", iso2: "video/mp4", iso4: "video/mp4", iso5: "video/mp4", iso6: "video/mp4", mp41: "video/mp4", mp42: "video/mp4",
  avc1: "video/mp4", dash: "video/mp4", mmp4: "video/mp4", "M4V ": "video/mp4", M4VP: "video/mp4", "f4v ": "video/mp4",
  "qt  ": "video/quicktime", "M4A ": "audio/mp4", "M4B ": "audio/mp4",
};
const ascii = (b: Uint8Array, from: number, to: number) => String.fromCharCode(...b.subarray(from, to));

/** MIME pelos bytes mágicos, só os de ACCEPTED_MIME; null = recusado. */
export function sniffMime(b: Uint8Array): string | null {
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (ascii(b, 0, 8) === "\x89PNG\r\n\x1a\n") return "image/png";
  if (/^GIF8[79]a$/.test(ascii(b, 0, 6))) return "image/gif";
  if (ascii(b, 0, 4) === "RIFF") return ({ WEBP: "image/webp", WAVE: "audio/wav" } as Record<string, string>)[ascii(b, 8, 12)] ?? null;
  // ponytail: AVIF com marca principal mif1 sai como image/heif e MP4 só de áudio (marca isom) sai como vídeo; os dois
  // tocam/aparecem do mesmo jeito.
  if (ascii(b, 4, 8) === "ftyp") return FTYP[ascii(b, 8, 12)] ?? null;
  if (ascii(b, 0, 5) === "%PDF-") return "application/pdf";
  // EBML: WebM declara o DocType "webm" logo no cabeçalho (Matroska .mkv fica de fora).
  if (b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return ascii(b, 4, 64).includes("webm") ? "video/webm" : null;
  if (ascii(b, 0, 4) === "OggS") return "audio/ogg";
  // MP3: etiqueta ID3 ou quadro MPEG camada III (o AAC cru, camada 00, fica de fora).
  if (ascii(b, 0, 3) === "ID3" || (b[0] === 0xff && (b[1] & 0xe6) === 0xe2)) return "audio/mpeg";
  // ponytail: comentário longo antes do <svg> (além de SNIFF_BYTES) é recusado.
  const text = new TextDecoder().decode(b.subarray(0, SNIFF_BYTES));
  return /^\s*(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*|<!DOCTYPE[^>]*>\s*)*<svg[\s>]/i.test(text) ? "image/svg+xml" : null;
}

/** Tipo do card para um MIME aceito (KIND_LABEL: imagem, pdf, vídeo, áudio). */
export function kindOfMime(mime: string): CardKind {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("video/")) return "video";
  return mime.startsWith("audio/") ? "audio" : "file";
}

/** Cards com arquivo próprio (blobId no card.create). */
export const BLOB_KINDS: readonly CardKind[] = ["image", "pdf", "video", "audio", "file"];

/**
 * accept dos seletores e do share_target. As extensões cobrem o MIME que o sistema dá a .m4a, .wav, .heic... (o filtro
 * do seletor no Windows é por extensão); o arquivo escolhido passa de novo pelos bytes mágicos.
 */
export const IMAGE_ACCEPT = [...ACCEPTED_MIME.filter((m) => m.startsWith("image/")), ".heic", ".heif", ".avif"].join(",");
/**
 * Seletor de fotos do "+" (Foto ou imagem): sem HEIC/HEIF. Sem eles no accept, o iPhone entrega as fotos da galeria já em
 * JPEG, que todo navegador mostra e a análise lê; HEIC continua aceito ao soltar, colar e compartilhar.
 */
export const PHOTO_ACCEPT = IMAGE_ACCEPT.split(",").filter((t) => !/hei[cf]$/.test(t)).join(",");
export const FILE_ACCEPT = [
  ...ACCEPTED_MIME.filter((m) => !m.startsWith("image/")),
  ".pdf", ".mp4", ".m4v", ".mov", ".webm", ".mp3", ".m4a", ".ogg", ".oga", ".opus", ".wav",
].join(",");
/** Nomes que o Android e outros apps dão aos mesmos formatos de ACCEPTED_MIME (manifest share_target). */
export const MIME_ALIASES = ["audio/x-m4a", "audio/x-wav", "audio/wave", "audio/mp3", "audio/opus"] as const;
